import { watch } from 'vue'

import { useLogger } from '@core/composables/useLogger'
import { mergeLayerLocales } from '@core/utils/i18n.utils'

import type { NuxtApp } from 'nuxt/app'

export interface I18nLoaderOptions {
  /** Function that returns layer locales for this app */
  getLayerLocales: () => Record<string, any>
  /** Use structured logging via useLogger. @default true */
  useStructuredLogging?: boolean
}

/**
 * i18n loader plugin - shared across all apps
 * Loads and merges layer locales for the app
 *
 * Usage: In each app's `plugins/i18n-loader.ts`, pass your `getLayerLocales` and export:
 * `export default defineNuxtPlugin(createI18nLoader({ ... }))` so Nuxt can statically detect
 * `defineNuxtPlugin` in the plugin file.
 * See apps/clientportal/plugins/i18n-loader.ts or apps/gokollab/plugins/i18n-loader.ts for examples.
 */
export function createI18nLoader(options: I18nLoaderOptions) {
  const { getLayerLocales, useStructuredLogging = true } = options

  return {
    name: 'i18n-loader',
    async setup(nuxtApp: NuxtApp) {
      const logger = useStructuredLogging ? useLogger().logger : null

      // Wait for i18n to be ready
      const i18n = nuxtApp.$i18n as any
      if (!i18n) return

      // @nuxtjs/i18n defaults fallbackLocale to `false` unless a vueI18n config file is
      // provided. Set it directly on the Composer so missing keys fall back to English
      // instead of rendering as raw key strings.
      if (i18n.fallbackLocale?.value !== undefined) {
        i18n.fallbackLocale.value = 'en'
      }

      const layerLocales = getLayerLocales()

      // Get current locale
      const currentLocale = typeof i18n.locale === 'string' ? i18n.locale : i18n.locale?.value || 'en'

      const onError = ({ path, error }: { path: string; error: Error }) => {
        if (logger) {
          logger.warn('[i18n-loader] Failed to merge layer locale', {
            labels: { path },
            error,
          })
        } else {
          console.warn('[i18n-loader] Failed to merge layer locale:', path, error)
        }
      }

      // Start with base translations from translation layer (already loaded by i18n module)
      const merged = mergeLayerLocales(layerLocales, currentLocale, i18n.getLocaleMessage(currentLocale) || {}, {
        onError,
      })

      // Set the merged translations
      i18n.setLocaleMessage(currentLocale, merged)

      // Always populate English messages so the fallback chain works.
      // apps/gokollab/locales/en.json is an empty placeholder — the real translations
      // live in layer locale files. If the user starts with a non-English locale, 'en'
      // is never merged and the fallback renders raw key strings.
      const loadedLocales = new Set([currentLocale])
      if (currentLocale !== 'en') {
        const enMerged = mergeLayerLocales(layerLocales, 'en', i18n.getLocaleMessage('en') || {}, { onError })
        i18n.setLocaleMessage('en', enMerged)
        loadedLocales.add('en')
      }

      // Handle locale changes on client side
      if (process.client && i18n.locale && typeof i18n.locale === 'object') {
        const loaded = loadedLocales

        watch(i18n.locale as any, async (newLocale: string) => {
          if (!loaded.has(newLocale)) {
            const newMerged = mergeLayerLocales(layerLocales, newLocale, i18n.getLocaleMessage(newLocale) || {}, {
              onError,
            })

            i18n.setLocaleMessage(newLocale, newMerged)
            loaded.add(newLocale)
          }
        })
      }
    },
  }
}

/**
 * Get merged layer locales for a specific locale.
 * Used by test setup to get translations for the test environment.
 */
export function getMergedLayerLocales(getLayerLocales: () => Record<string, any>, locale: string): Record<string, unknown> {
  const layerLocales = getLayerLocales()
  return mergeLayerLocales(layerLocales, locale, {})
}
